import React, {useEffect, useState} from 'react';
import {XAxis, YAxis, CartesianGrid, Tooltip, Legend} from 'recharts';
import {Line, LineChart, ResponsiveContainer} from 'recharts';
import {status} from '../../../constant/status';
import {TableHeader} from '../../../components/header/TableHeader';
import {isFacilityEmployee} from '../../../utils/checkEmployerType';
import {AutoCompleteFilter} from 'src/components/filter/Filters';
import {routeLink} from 'src/constant';
import {useFilter} from '@unthinkable/react-filters';
import {useAuth} from 'src/modules/auth/hooks/useAuth';
import {useToast} from '@unthinkable/react-toast';
import {HttpAuthService} from 'src/services';
import {isAdmin} from 'src/utils/checkUserType';
import {ActivityIndicator} from '@unthinkable/react-activity-indicator';
import {ChartContainer, LoaderContainer} from '../styles/stackedGraph.style';

const PracticeStatusChart = () => {
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const {user} = useAuth();
  const toast = useToast();
  const filterProps = useFilter({
    params: {
      type: 'All',
    },
    filter: {},
  });
  const {onChangeFilter, filterValues} = filterProps;
  const {filter} = filterValues || {};
  const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded shadow text-sm">
          <p className="font-semibold">{label}</p>
          <p>TCMCompletion: {data.tcmCompletionRatio.toLocaleString()}%</p>
          <p>Readmission: {data.reAdmission.toLocaleString()}%</p>
          <p>
            HospitalizationRate: {data.currentHospitalization.toLocaleString()}%
          </p>
        </div>
      );
    }
    return null;
  };

  const getStats = async filter => {
    const response = await HttpAuthService.get(routeLink.getQualityMetrics, {
      params: {
        filter: JSON.stringify({...filter}),
      },
    });
    return response;
  };
  const fetchStats = async () => {
    try {
      setLoading(true);
      let newFilter = {...filter, scope: 'app'};
      if (user.practiceId) {
        newFilter.practiceId = user.practiceId?._id;
        newFilter.scope = 'practice';
      } else if (user.facilityId) {
        newFilter.facilityId = user.facilityId?._id;
        newFilter.scope = 'facility';
      }
      if (newFilter.practiceId) newFilter.scope = 'practice';
      if (newFilter.facilityId) newFilter.scope = 'facility';

      const response = await getStats(newFilter);
      if (response?.data) {
        setStatsData(response?.data);
      }
    } catch (err) {
      console.log('err', err);
      toast({
        type: 'Error',
        message: `${err?.message || err || ''}`,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [filter, filterValues]);

  return (
    <ChartContainer>
      <TableHeader
        title={isFacilityEmployee() ? 'Facility Status' : 'Quality Metrics'}
        actions={[
          !isFacilityEmployee() && !isAdmin() && (
            <AutoCompleteFilter
              onChangeFilter={onChangeFilter}
              filterValues={filterValues}
              field={'facilityId'}
              filter={() => {
                return {
                  ...(user.practiceId ? {practices: user.practiceId} : {}),
                  status: status.active,
                };
              }}
              api={routeLink.getFacilities}
              key={'facilityFilter'}
              placeholder={'Facility'}
              suggestionField={'name'}
              sort={{name: 1}}
            />
          ),
          isAdmin() && (
            <AutoCompleteFilter
              onChangeFilter={onChangeFilter}
              filterValues={filterValues}
              field={'practiceId'}
              filter={() => {
                return {
                  status: status.active,
                };
              }}
              api={routeLink.practiceLink}
              key={'PracticeFilter'}
              placeholder={'Practice'}
              suggestionField={'name'}
              sort={{name: 1}}
            />
          ),
        ]}
      />
      {loading ? (
        <LoaderContainer>
          <ActivityIndicator />
        </LoaderContainer>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={statsData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            {statsData.length > 0 && <Tooltip content={<CustomTooltip />} />}
            <Legend />
            <Line
              type="monotone"
              dataKey="tcmCompletionRatio"
              stroke="#8884d8"
              activeDot={{r: 8}}
            />
            <Line type="monotone" dataKey="reAdmission" stroke="#82ca9d" />
            <Line
              type="monotone"
              dataKey="currentHospitalization"
              stroke="#039be5"
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartContainer>
  );
};

export default PracticeStatusChart;
